/**
 * Grouped invoice drafts — the `invoice_drafts` collection behind the Invoice
 * builder. The rules (rounding, totals, which entries a draft may use) are in
 * lib/invoiceDraft; this is only reading and writing the rows.
 *
 * Saves write the returned row straight into the cache rather than refetching:
 * the builder saves as you type, and a refetch landing mid-keystroke would put
 * back the text from a moment ago.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { pb } from "@/lib/pocketbase";
import type { InvoiceDraft } from "@/lib/types";

const userId = (): string => (pb.authStore.model as { id?: string } | null)?.id ?? "";
const KEY = ["invoice_drafts"];

/** A json field comes back null when unset; everything downstream wants an array. */
const clean = (r: InvoiceDraft): InvoiceDraft => ({ ...r, lines: Array.isArray(r.lines) ? r.lines : [] });

export function useInvoiceDrafts() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await pb.collection("invoice_drafts").getFullList<InvoiceDraft>({ sort: "-month,-updated" })).map(clean),
  });
}

export function useInvoiceDraftMutations() {
  const qc = useQueryClient();
  const put = (row: InvoiceDraft) =>
    qc.setQueryData<InvoiceDraft[]>(KEY, (rows = []) => {
      const r = clean(row);
      return rows.some((x) => x.id === r.id) ? rows.map((x) => (x.id === r.id ? r : x)) : [r, ...rows];
    });

  const createDraft = useMutation({
    mutationFn: (data: Omit<InvoiceDraft, "id" | "user" | "created" | "updated">) =>
      pb.collection("invoice_drafts").create<InvoiceDraft>({ ...data, user: userId() }),
    onSuccess: put,
  });

  const saveDraft = useMutation({
    mutationFn: ({ id, ...patch }: Partial<InvoiceDraft> & { id: string }) =>
      pb.collection("invoice_drafts").update<InvoiceDraft>(id, patch),
    onSuccess: put,
  });

  const deleteDraft = useMutation({
    mutationFn: (id: string) => pb.collection("invoice_drafts").delete(id),
    onSuccess: (_r, id) => qc.setQueryData<InvoiceDraft[]>(KEY, (rows = []) => rows.filter((x) => x.id !== id)),
  });

  return { createDraft, saveDraft, deleteDraft };
}
